import { PageHeaderWrapper, type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { FullScreenDialog } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { PageHeaderTopRow } from "@src/appV2/redesign/components/PageHeaderTopRow";
import { type PageHeaderTopRowProps } from "@src/appV2/redesign/components/PageHeaderTopRow";
import { useRequirementDetailsContext } from "@src/appV2/redesign/Documents/Details/useRequirementDetailsContext";
import { DOCUMENT_DETAILS_FULL_PATH } from "@src/appV2/redesign/Documents/paths";
import { generatePath, Redirect } from "react-router-dom";

import { isTbTestGuidedUploadRequirement } from "../constants";
import { useTbTestGuidedUploadWorkflow } from "../hooks/useTbTestGuidedUploadWorkflow";
import { type TbTestGuidedUploadStep } from "../types";
import { DateValidationStep } from "./DateValidationStep";
import { FilePreviewStep } from "./FilePreviewStep";
import { InstructionsStep } from "./InstructionsStep";
import { InvalidTestErrorStep } from "./InvalidTestErrorStep";
import { SuccessStep } from "./SuccessStep";
import { TestTypeSelectionStep } from "./TestTypeSelectionStep";
import { UploadStep } from "./UploadStep";

interface TbTestGuidedUploadDialogProps {
  modalState: UseModalState;
}

export function TbTestGuidedUploadDialog(props: TbTestGuidedUploadDialogProps) {
  const { modalState } = props;

  const { requirement } = useRequirementDetailsContext();
  const { requirementId } = requirement;

  const {
    step,
    selectedOption,
    setSelectedOption,
    testType,
    selectedTbTestDate,
    isUploading,
    hasFiles,
    handleTestTypeSelectionConfirmed,
    previewFile,
    handleNext,
    handleBack,
    handleClose,
    handlePreviewFile,
    handleReplaceFile,
    handleDeletePreviewedFile,
    handleDateChange,
    handleTbTestDateSubmitted,
    reset,
  } = useTbTestGuidedUploadWorkflow();

  useLogEffect(
    APP_V2_USER_EVENTS.VIEWED_TB_TEST_GUIDED_UPLOAD_DIALOG,
    { requirementId },
    { enabled: modalState.modalIsOpen }
  );

  // This shouldn't happen in normal navigation, but if someone tries to open
  // this dialog for a requirement that isn't a TB Test, redirect back to details.
  if (!isTbTestGuidedUploadRequirement(requirement)) {
    return <Redirect to={generatePath(DOCUMENT_DETAILS_FULL_PATH, { requirementId })} />;
  }

  function renderStep() {
    switch (step) {
      case "test-type-selection": {
        return (
          <TestTypeSelectionStep
            selectedOption={selectedOption}
            onSelectedOptionChange={setSelectedOption}
            onConfirm={handleTestTypeSelectionConfirmed}
            onBack={handleBack}
          />
        );
      }
      case "instructions": {
        if (!isDefined(testType)) {
          return null;
        }
        return <InstructionsStep testType={testType} onNext={handleNext} onBack={handleBack} />;
      }
      case "upload": {
        return (
          <UploadStep
            hasFiles={hasFiles}
            onNext={handleNext}
            onBack={handleBack}
            onFilePreview={handlePreviewFile}
          />
        );
      }
      case "file-preview": {
        if (!isDefined(previewFile)) {
          return null;
        }

        return (
          <FilePreviewStep
            file={previewFile}
            onReplace={handleReplaceFile}
            onDelete={handleDeletePreviewedFile}
          />
        );
      }
      case "date-validation": {
        return (
          <DateValidationStep
            selectedDate={selectedTbTestDate}
            isUploading={isUploading}
            onDateChange={handleDateChange}
            onSubmit={handleTbTestDateSubmitted}
            onBack={handleBack}
          />
        );
      }
      case "invalid-test-error": {
        if (!isDefined(testType)) {
          return null;
        }
        return (
          <InvalidTestErrorStep requirementId={requirementId} testType={testType} onReset={reset} />
        );
      }
      case "success": {
        return (
          <SuccessStep requirementId={requirementId} testType={testType} onDone={handleNext} />
        );
      }
      default: {
        return step satisfies never; // Exhaustive check to ensure all steps are handled
      }
    }
  }

  function getPageHeaderTopRowProps(
    currentStep: TbTestGuidedUploadStep
  ): Partial<PageHeaderTopRowProps> {
    switch (currentStep) {
      case "file-preview": {
        return {
          hideBackButton: false,
          title: "Preview",
          onClickBack: handleBack,
        };
      }
      default: {
        return {
          hideBackButton: true,
          rightAction: (
            <IconButton
              aria-label="Close"
              disabled={isUploading}
              iconType="close"
              variant="outlined"
              onClick={handleClose}
            />
          ),
        };
      }
    }
  }

  return (
    <FullScreenDialog skipTopSafeAreaPadding modalState={modalState} variant="quaternary">
      <PageHeaderWrapper variant="quaternary">
        <PageHeaderTopRow {...getPageHeaderTopRowProps(step)} />
      </PageHeaderWrapper>

      {renderStep()}
    </FullScreenDialog>
  );
}
